import axios, { AxiosError, ResponseType as AxiosResponseType } from 'axios';
import Constants from '../constants';

const DEFAULT_ERROR_MESSAGE = 'Something went wrong.';

interface Options {
  body?: any;
  throwErrorAsString?: boolean;
  returnNestedResponseData?: boolean;
  withCredentials?: boolean;
  responseType?: AxiosResponseType;
  /**
   * `timeout` specifies the number of milliseconds before the request times out.
   * If the request takes longer than `timeout`, the request will be aborted.
   */
  timeout?: number;
}

const DEFAULT_OPTIONS: Options = {
  throwErrorAsString: true,
  returnNestedResponseData: true,
};

const { internalApiHost } = Constants;

const client = axios.create({
  baseURL: internalApiHost,
});

let axiosInterceptors: number[] = [];

const extractErrorMessage = (error: Error | AxiosError) => {
  if (typeof error === 'string') return error;

  if (axios.isAxiosError(error))
    return (
      error.response?.data?.error_message ||
      (error.response?.data?.errors?.length &&
        error.response?.data?.errors[0]) ||
      error.response?.data?.message ||
      error.message ||
      error
    );

  return error?.message || error;
};

export function initializeAxiosClient(accessToken: string | null) {
  // When our access token changes, then our axios interceptors need to be recreated to use the
  // new access token. So first we eject all the existing interceptors before replacing them
  // with the new ones.
  for (const interceptor of axiosInterceptors) {
    client.interceptors.request.eject(interceptor);
  }

  // Reset the interceptors array
  axiosInterceptors = [];

  const accessTokenInterceptor = client.interceptors.request.use(
    ({ headers, ...config }) => {
      return {
        ...config,
        headers: {
          ...headers,
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      };
    },
  );

  const responseInterceptor = client.interceptors.response.use(
    (data) => {
      if (data.data?.success === false) {
        throw new Error(
          data.data.error || data.data.message || DEFAULT_ERROR_MESSAGE,
        );
      }

      return data;
    },
    async (error) => {
      throw error;
    },
  );

  axiosInterceptors.push(accessTokenInterceptor, responseInterceptor);
}

export const api = {
  get: async <TResponse = any>(
    url: string,
    options?: Options,
  ): Promise<TResponse> => {
    const {
      returnNestedResponseData,
      throwErrorAsString,
      responseType,
      timeout,
    } = {
      ...DEFAULT_OPTIONS,
      ...options,
    };

    try {
      const response = await client.get(url, {
        responseType,
        timeout,
      });
      return returnNestedResponseData ? response.data : response;
    } catch (error: any) {
      if (throwErrorAsString) throw extractErrorMessage(error);
      throw error;
    }
  },
  post: async <TResponse = any>(
    url: string,
    body = {},
    options?: Options,
  ): Promise<TResponse> => {
    const { returnNestedResponseData, throwErrorAsString, withCredentials } = {
      ...DEFAULT_OPTIONS,
      ...options,
    };

    const config = {
      ...(withCredentials ? { withCredentials: true } : {}),
    };

    try {
      const response = await client.post(url, body, config);
      return returnNestedResponseData ? response.data : response;
    } catch (error: any) {
      if (throwErrorAsString) throw extractErrorMessage(error);
      throw error;
    }
  },
  put: async <TResponse = any>(
    url: string,
    body = {},
    options?: Options,
  ): Promise<TResponse> => {
    const { returnNestedResponseData, throwErrorAsString } = {
      ...DEFAULT_OPTIONS,
      ...options,
    };

    try {
      const response = await client.put(url, body);
      return returnNestedResponseData ? response.data : response;
    } catch (error: any) {
      if (throwErrorAsString) throw extractErrorMessage(error);
      throw error;
    }
  },
  patch: async <TResponse = any>(
    url: string,
    body = {},
    options?: Options,
  ): Promise<TResponse> => {
    const { returnNestedResponseData, throwErrorAsString } = {
      ...DEFAULT_OPTIONS,
      ...options,
    };

    try {
      const response = await client.patch(url, body);
      return returnNestedResponseData ? response.data : response;
    } catch (error: any) {
      if (throwErrorAsString) throw extractErrorMessage(error);
      throw error;
    }
  },
  delete: async <TResponse = any>(
    url: string,
    options?: Options,
  ): Promise<TResponse> => {
    const { returnNestedResponseData, throwErrorAsString, body } = {
      ...DEFAULT_OPTIONS,
      ...options,
    };

    try {
      const response = await client.delete(url, { data: body });
      return returnNestedResponseData ? response.data : response;
    } catch (error: any) {
      if (throwErrorAsString) throw extractErrorMessage(error);
      throw error;
    }
  },
  postFormData: async <TResponse = any>(
    url: string,
    formData: FormData,
    options?: Options,
  ): Promise<TResponse> => {
    const { returnNestedResponseData, throwErrorAsString } = {
      ...DEFAULT_OPTIONS,
      ...options,
    };

    try {
      const response = await client.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return returnNestedResponseData ? response.data : response;
    } catch (error: any) {
      if (throwErrorAsString) throw extractErrorMessage(error);
      throw error;
    }
  },
};
